'use client';

import React, { useState, useEffect } from 'react';
import { useCurrentVariant } from '@/ui/hooks/productDetail/useCurrentVariant';
import { getUrlParamValue } from '@/ui/utils/client-url-helpers';
import { urlParameterCodes } from '@/constants/urlParams';
import { TileFragmentFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { useLocalization } from '@/ui/providers/localizationProvider';
import {
  filterHiddenByCountry,
  getSoleAvailableOption,
  isHiddenByCountry,
} from '@/utils/tile-country-visibility';
import { ProductAlternativeModelsSection } from '../ProductAlternativeModelsSection';
import DoorKnobSelection from './DoorKnobSection';
import { BundlesSection } from '../bundles-section/bundles-section';
import { ColorPicker } from '../color-picker/color-picker';
import { CylinderSection } from './CylinderSection';
import { ParsedSmartlockVariant } from './types/ParsedSmartlockVariant';
import { useBundleContext } from '../../../../providers/bundleProvider';

import ProductChooserWrapper from '../product-chooser-wrapper/product-chooser-wrapper';
import { Smartlock } from '../../../../services/SmartlockService';
import { useSmartLockContext } from '../../../../providers/smartlockProvider';

function SmartlockDetail({
  smartlockProduct,
  upgradeProgramDeviceId,
}: Readonly<{
  smartlockProduct: Smartlock;
  upgradeProgramDeviceId?: string;
}>) {
  const {
    selectedBundle,
    selectedVariants: selectedBundleProductVariants,
    handleSelectVariant,
  } = useBundleContext();
  const { hasDoorKnob, selectedManufacturer } = useSmartLockContext();
  const { country } = useLocalization();

  const { currentVariant, setSelectedVariant } = useCurrentVariant<ParsedSmartlockVariant>(
    smartlockProduct,
    true,
    handleSelectVariant,
    selectedBundle,
  );

  const [additionalInformation, setAdditionalInformation] =
    useState<TileFragmentFragment[]>();
  const [activeBoxId, setActiveBoxId] = useState<string | undefined>();

  const openAdditionalInformation = (
    tiles?: TileFragmentFragment[],
    boxId?: string,
  ) => {
    setAdditionalInformation(tiles);
    setActiveBoxId(boxId);
    if (globalThis.location.hash !== '#additional-information') {
      globalThis.history.pushState(
        {},
        '',
        `${globalThis.location.pathname}${globalThis.location.search}#additional-information`,
      );
    }
  };

  const closeAdditionalInformation = () => {
    setAdditionalInformation(undefined);
    setActiveBoxId(undefined);
    if (globalThis.location.hash === '#additional-information') {
      globalThis.history.replaceState(
        {},
        '',
        `${globalThis.location.pathname}${globalThis.location.search}`,
      );
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      if (additionalInformation) {
        setAdditionalInformation(undefined);
        setActiveBoxId(undefined);
      }
    };
    globalThis.addEventListener('popstate', handlePopState);
    return () => globalThis.removeEventListener('popstate', handlePopState);
  }, [additionalInformation]);

  const isSubscriptionChooser = !!getUrlParamValue(urlParameterCodes.productDetail.subscription);
  const isUpgradeChooser = !!getUrlParamValue(urlParameterCodes.productDetail.upgrade);

  // When color or cylinder changes, get the new set of images
  const updateVariant = (color?: string, cylinder?: string) => {
    const selectedVariant = smartlockProduct.getSelectedVariant({ color, cylinder });
    if (selectedVariant && selectedVariant.sku !== currentVariant.sku) {
      // if selected variant is not already selected
      setSelectedVariant(selectedVariant);
    }
  };

  // Drop country-restricted cylinders/models configured to "hide"; the ones
  // kept in "show_message" mode surface the existing unavailability notice.
  const visibleCylinders = filterHiddenByCountry(smartlockProduct.availableCylinders, country);

  // The Smart Lock's own row in the bundle configurator mirrors the cylinder picker.
  const hostSlot = smartlockProduct.getBundleHostSlot(
    currentVariant,
    visibleCylinders,
    updateVariant,
  );
  const visibleAlternativeModels = smartlockProduct.alternativeModelSection
    ? filterHiddenByCountry(smartlockProduct.alternativeModelSection, country)
    : undefined;

  // When exactly one cylinder is fully available for the country, auto-select it
  // so the visitor isn't left without a valid selection. A "show_message"
  // cylinder still renders and stays selectable: we only auto-snap when the
  // current selection is unset or itself country-hidden, so picking a
  // restricted-with-message tile is respected instead of bouncing back. The
  // type-mismatch guard also keeps the effect a no-op once selected.
  const soleCylinder = getSoleAvailableOption(smartlockProduct.availableCylinders, country);

  useEffect(() => {
    const { cylinder } = currentVariant;
    const shouldAutoSelect = !cylinder || isHiddenByCountry(cylinder, country);

    if (soleCylinder && shouldAutoSelect && cylinder?.type !== soleCylinder.type) {
      updateVariant(currentVariant.color?.name, soleCylinder.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soleCylinder?.type, currentVariant.cylinder?.type, currentVariant.color?.name, country]);

  const galleryImages =
    selectedBundle && !isUpgradeChooser
      ? selectedBundle.getImagesForSelection(selectedBundleProductVariants)
      : currentVariant.images;

  // Bundle video is not fetched yet, so only variant-level videos are shown for now.
  const galleryVideos = selectedBundle && !isUpgradeChooser ? [] : currentVariant.videos;

  const addToCart = () => {
    if (!currentVariant) {
      return; // TODO show err
    }

    // const validPriceVariant = parsePriceVariant(currentVariant.priceVariant);
    if (!currentVariant.priceVariant) {
      return; // TODO show error - missing price variant?
    }

    closeAdditionalInformation();
    smartlockProduct.addToCart(currentVariant, {
      hasDoorKnob,
      selectedManufacturer,
      selectedBundle,
      selectedBundleProductVariants,
      upgradeProgramDeviceId,
    });
  };

  return (
    <ProductChooserWrapper
      product={smartlockProduct}
      currentVariant={currentVariant}
      selectedBundle={selectedBundle}
      selectedBundleProductVariants={selectedBundleProductVariants}
      shopFeatures={smartlockProduct.shopFeatures}
      addToCart={addToCart}
      galleryImages={galleryImages}
      videos={galleryVideos}
      onAdditionalInformationClose={closeAdditionalInformation}
      additionalInformationTiles={additionalInformation}
    >
      {visibleAlternativeModels && visibleAlternativeModels.length > 0 && (
        <ProductAlternativeModelsSection
          sectionHeading={
            isSubscriptionChooser && smartlockProduct.alternativeModelSubscriptionHeading
              ? smartlockProduct.alternativeModelSubscriptionHeading
              : smartlockProduct.alternativeModelHeading
          }
          alternativeProductModels={visibleAlternativeModels}
          currentProductPath={smartlockProduct.path}
          displayOnlyCurrentProduct={isSubscriptionChooser}
          openAdditionalInformation={openAdditionalInformation}
          closeAdditionalInformation={closeAdditionalInformation}
          activeBoxId={activeBoxId}
        />
      )}
      {!isUpgradeChooser && !smartlockProduct.hideColorSelection && (
        <ColorPicker
          sectionHeading={smartlockProduct.colorHeading}
          availableColors={smartlockProduct.availableColors}
          selectedColor={currentVariant.color}
          setSelectedColor={(color) => updateVariant(color.name, currentVariant.cylinder?.type)}
        />
      )}
      <CylinderSection
        sectionHeading={smartlockProduct.cylinderHeading}
        availableCylinders={visibleCylinders}
        selectedCylinder={currentVariant.cylinder}
        setSelectedCylinder={(cylinder) => updateVariant(currentVariant.color?.name, cylinder.type)}
      />
      {!isUpgradeChooser && <DoorKnobSelection data={smartlockProduct.doorKnob} />}
      {!isSubscriptionChooser &&
        !isUpgradeChooser &&
        smartlockProduct.bundles &&
        smartlockProduct.bundles.length > 0 && (
          <BundlesSection
            sectionHeading={smartlockProduct.bundlesHeading}
            bundles={smartlockProduct.bundles}
            hostSlot={hostSlot}
            openAdditionalInformation={openAdditionalInformation}
            closeAdditionalInformation={closeAdditionalInformation}
            activeBoxId={activeBoxId}
          />
      )}

      {isSubscriptionChooser &&
        smartlockProduct.subscriptionBundles &&
        smartlockProduct.subscriptionBundles.length > 0 && (
          <BundlesSection
            sectionHeading={smartlockProduct.subscriptionHeading}
            bundles={smartlockProduct.subscriptionBundles}
            openAdditionalInformation={openAdditionalInformation}
            closeAdditionalInformation={closeAdditionalInformation}
            activeBoxId={activeBoxId}
            isSubscription
          />
      )}

      {isUpgradeChooser &&
        smartlockProduct.upgradeBundles &&
        smartlockProduct.upgradeBundles.length > 0 && (
          <BundlesSection
            sectionHeading={smartlockProduct.upgradeHeading}
            bundles={smartlockProduct.upgradeBundles}
            openAdditionalInformation={openAdditionalInformation}
            closeAdditionalInformation={closeAdditionalInformation}
            activeBoxId={activeBoxId}
          />
      )}
    </ProductChooserWrapper>
  );
}

export default SmartlockDetail;
